/**
 * Memory Game Admin - Image Management Screen
 * Allows admins to manage which images are used in the Memory game.
 * Options: Standard images or family photos (from weekend gallery + new uploads).
 */

import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  Alert,
  ActivityIndicator,
  Image,
  Dimensions,
  RefreshControl,
  TextInput,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useRouter } from "expo-router";
import * as ImagePicker from "expo-image-picker";
import * as ImageManipulator from "expo-image-manipulator";
import { readAsStringAsync } from "expo-file-system/legacy";
import { decode } from "base64-arraybuffer";
import { useThemeColors } from "@/providers/ThemeProvider";
import { useAuth } from "@/providers/AuthProvider";
import { supabase } from "@/lib/supabase/client";
import { RANDOM_IMAGES } from "@/modules/memory";

/** Static preview images for the standard set */
const STANDARD_PREVIEW_IMAGES = [
  require("@/assets/memory_game_images/robot.jpg"),
  require("@/assets/memory_game_images/spiderman.jpg"),
  require("@/assets/memory_game_images/minion_hero.jpg"),
  require("@/assets/memory_game_images/r2d2.jpg"),
  require("@/assets/memory_game_images/toystory.jpg"),
  require("@/assets/memory_game_images/friendly_blue_monster.jpg"),
];

/** Image grid size calculation */
const SCREEN_WIDTH = Dimensions.get("window").width;
const GRID_PADDING = 32; // px-4 each side
const GRID_GAP = 8;
const COLUMNS = 3;
const IMAGE_SIZE = (SCREEN_WIDTH - GRID_PADDING - GRID_GAP * (COLUMNS - 1)) / COLUMNS;

/** Type for the active image set source */
type ImageSource = "standard" | "family";

/** Family image set data from the database */
interface FamilyImageSet {
  id: string;
  name: string;
  description: string | null;
  type: string;
  images: string[];
  weekend_id: string | null;
  is_active: boolean;
  created_at: string;
}

/** Weekend photo from the files table */
interface WeekendPhoto {
  id: string;
  file_url: string;
  thumbnail_url: string | null;
  created_at: string;
  uploader?: {
    id: string;
    full_name: string;
    nickname: string | null;
    avatar_url: string | null;
  };
}

/**
 * Memory Admin Screen
 */
/** Memory points configuration */
interface MemoryPointsConfig {
  completion: number;
  speed_bonus: number;
  perfect_memory_bonus: number;
}

const DEFAULT_MEMORY_POINTS: MemoryPointsConfig = {
  completion: 5,
  speed_bonus: 3,
  perfect_memory_bonus: 10,
};

export default function MemoryAdminScreen() {
  const router = useRouter();
  const colors = useThemeColors();
  const { activeWeekend, user } = useAuth();

  const [imageSource, setImageSource] = useState<ImageSource>("standard");
  const [familyImages, setFamilyImages] = useState<string[]>([]);
  const [familyImageSet, setFamilyImageSet] = useState<FamilyImageSet | null>(null);
  const [weekendPhotos, setWeekendPhotos] = useState<WeekendPhoto[]>([]);
  const [adminPhotos, setAdminPhotos] = useState<WeekendPhoto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingPhotos, setIsLoadingPhotos] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pointsConfig, setPointsConfig] = useState<MemoryPointsConfig>(DEFAULT_MEMORY_POINTS);
  const [showPoints, setShowPoints] = useState(false);

  /** Set of selected weekend photo URLs (for quick lookup) */
  const selectedUrlsSet = useMemo(() => new Set(familyImages), [familyImages]);

  /**
   * Fetch existing image sets and points config from the database
   */
  const fetchImageSets = useCallback(async () => {
    try {
      const { data: familySets, error } = await supabase
        .from("memory_image_sets")
        .select("*")
        .eq("type", "family")
        .order("created_at", { ascending: false })
        .limit(1);

      if (error) throw error;

      if (familySets && familySets.length > 0) {
        const set = familySets[0] as FamilyImageSet;
        setFamilyImageSet(set);
        setFamilyImages(set.images || []);
        setImageSource(set.is_active ? "family" : "standard");
      } else {
        setImageSource("standard");
        setFamilyImages([]);
        setFamilyImageSet(null);
      }

      if (activeWeekend?.id) {
        const { data: configData } = await supabase
          .from("weekend_game_config")
          .select("points_config")
          .eq("weekend_id", activeWeekend.id)
          .eq("game_slug", "puzzle_matching")
          .maybeSingle();

        if (configData?.points_config) {
          const pc = configData.points_config as Partial<MemoryPointsConfig>;
          setPointsConfig({
            completion: pc.completion ?? DEFAULT_MEMORY_POINTS.completion,
            speed_bonus: pc.speed_bonus ?? DEFAULT_MEMORY_POINTS.speed_bonus,
            perfect_memory_bonus: pc.perfect_memory_bonus ?? DEFAULT_MEMORY_POINTS.perfect_memory_bonus,
          });
        }
      }
    } catch (err) {
      console.error("Error fetching image sets:", err);
    } finally {
      setIsLoading(false);
    }
  }, [activeWeekend?.id]);

  /**
   * Fetch photos: weekend gallery + admin-owned photos
   */
  const fetchWeekendPhotos = useCallback(async () => {
    setIsLoadingPhotos(true);
    try {
      let weekendPhotoIds = new Set<string>();

      // 1. Fetch weekend photos (all users, linked to active weekend)
      if (activeWeekend?.id) {
        const { data: weekendData, error: weekendError } = await supabase
          .from("files")
          .select(`
            id, file_url, thumbnail_url, created_at,
            uploader:profiles!files_uploaded_by_fkey(id, full_name, nickname, avatar_url)
          `)
          .eq("category", "photo")
          .eq("weekend_id", activeWeekend.id)
          .eq("is_visible", true)
          .order("created_at", { ascending: false });

        if (!weekendError && weekendData) {
          const photos = weekendData as unknown as WeekendPhoto[];
          setWeekendPhotos(photos);
          weekendPhotoIds = new Set(photos.map((p) => p.id));
        }
      }

      // 2. Fetch admin user IDs (super_admin + familie_admin)
      const { data: adminRoles, error: rolesError } = await supabase
        .from("user_roles")
        .select("user_id")
        .in("role", ["super_admin", "familie_admin"])
        .is("revoked_at", null);

      if (rolesError) throw rolesError;

      const adminUserIds = [...new Set((adminRoles || []).map((r) => r.user_id))];

      // Also include the current user
      if (user?.id && !adminUserIds.includes(user.id)) {
        adminUserIds.push(user.id);
      }

      if (adminUserIds.length > 0) {
        // Fetch ALL photos by admins/current user (not limited to the current weekend)
        const { data: allAdminPhotos, error: adminError } = await supabase
          .from("files")
          .select(`
            id, file_url, thumbnail_url, created_at,
            uploader:profiles!files_uploaded_by_fkey(id, full_name, nickname, avatar_url)
          `)
          .eq("category", "photo")
          .eq("is_visible", true)
          .in("uploaded_by", adminUserIds)
          .order("created_at", { ascending: false })
          .limit(100);

        if (!adminError && allAdminPhotos) {
          // Filter out photos already shown in the weekend section
          const uniqueAdminPhotos = (allAdminPhotos as unknown as WeekendPhoto[]).filter(
            (p) => !weekendPhotoIds.has(p.id)
          );
          setAdminPhotos(uniqueAdminPhotos);
        }
      }
    } catch (err) {
      console.error("Error fetching photos:", err);
    } finally {
      setIsLoadingPhotos(false);
    }
  }, [activeWeekend?.id, user?.id]);

  useEffect(() => {
    fetchImageSets();
  }, [fetchImageSets]);

  useEffect(() => {
    fetchWeekendPhotos();
  }, [fetchWeekendPhotos]);

  /**
   * Handle pull-to-refresh
   */
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    await Promise.all([fetchImageSets(), fetchWeekendPhotos()]);
    setIsRefreshing(false);
  }, [fetchImageSets, fetchWeekendPhotos]);

  /**
   * Toggle a weekend photo in/out of the selected set
   */
  const handleTogglePhoto = useCallback((url: string) => {
    setFamilyImages((prev) => {
      if (prev.includes(url)) {
        return prev.filter((img) => img !== url);
      }
      return [...prev, url];
    });
  }, []);

  /**
   * Select all weekend photos at once
   */
  const handleSelectAllWeekendPhotos = useCallback(() => {
    const allUrls = weekendPhotos.map((p) => p.file_url);
    setFamilyImages((prev) => {
      const existing = new Set(prev);
      const newUrls = allUrls.filter((url) => !existing.has(url));
      return [...prev, ...newUrls];
    });
  }, [weekendPhotos]);

  /**
   * Deselect all weekend photos
   */
  const handleDeselectAllWeekendPhotos = useCallback(() => {
    const weekendUrls = new Set(weekendPhotos.map((p) => p.file_url));
    setFamilyImages((prev) => prev.filter((url) => !weekendUrls.has(url)));
  }, [weekendPhotos]);

  /**
   * Pick and upload new images from the gallery
   */
  const handlePickImages = useCallback(async () => {
    try {
      const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (status !== "granted") {
        Alert.alert("Toestemming nodig", "We hebben toegang tot je foto's nodig om afbeeldingen te uploaden.");
        return;
      }

      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ["images"],
        allowsMultipleSelection: true,
        quality: 0.8,
        selectionLimit: 20,
      });

      if (result.canceled || result.assets.length === 0) return;

      setIsUploading(true);
      const uploadedUrls: string[] = [];

      for (const asset of result.assets) {
        try {
          const manipulated = await ImageManipulator.manipulateAsync(
            asset.uri,
            [{ resize: { width: 400, height: 400 } }],
            { compress: 0.8, format: ImageManipulator.SaveFormat.JPEG }
          );

          const base64 = await readAsStringAsync(manipulated.uri, { encoding: "base64" });
          const fileName = `memory/${activeWeekend?.id || "general"}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.jpg`;

          const { error: uploadError } = await supabase.storage
            .from("memory-images")
            .upload(fileName, decode(base64), {
              contentType: "image/jpeg",
              upsert: false,
            });

          if (uploadError) {
            console.error("Upload error:", uploadError);
            continue;
          }

          const { data: urlData } = supabase.storage
            .from("memory-images")
            .getPublicUrl(fileName);

          if (urlData?.publicUrl) {
            uploadedUrls.push(urlData.publicUrl);
          }
        } catch (err) {
          console.error("Error uploading image:", err);
        }
      }

      if (uploadedUrls.length > 0) {
        setFamilyImages((prev) => [...prev, ...uploadedUrls]);
        Alert.alert(
          "Gelukt",
          `${uploadedUrls.length} foto${uploadedUrls.length > 1 ? "'s" : ""} geüpload`
        );
      }
    } catch (err) {
      console.error("Error picking images:", err);
      Alert.alert("Fout", "Kon foto's niet selecteren");
    } finally {
      setIsUploading(false);
    }
  }, [activeWeekend?.id]);

  /**
   * Remove an image from the selected set
   */
  const handleRemoveImage = useCallback((url: string) => {
    setFamilyImages((prev) => prev.filter((img) => img !== url));
  }, []);

  /**
   * Save the image set configuration
   */
  const handleSave = useCallback(async () => {
    setIsSaving(true);

    try {
      if (imageSource === "family") {
        if (familyImages.length < 6) {
          Alert.alert(
            "Te weinig foto's",
            "Je hebt minimaal 6 foto's nodig voor het Memory spel (makkelijk modus heeft 6 paren)."
          );
          setIsSaving(false);
          return;
        }

        if (familyImageSet) {
          const { error } = await supabase
            .from("memory_image_sets")
            .update({
              images: familyImages,
              is_active: true,
              weekend_id: activeWeekend?.id || null,
            })
            .eq("id", familyImageSet.id);

          if (error) throw error;
        } else {
          const { error } = await supabase
            .from("memory_image_sets")
            .insert({
              name: "Familie Foto's",
              description: "Foto's van het familie weekend",
              type: "family",
              images: familyImages,
              is_active: true,
              weekend_id: activeWeekend?.id || null,
            });

          if (error) throw error;
        }

        await supabase
          .from("memory_image_sets")
          .update({ is_active: false })
          .eq("type", "random");
      } else {
        if (familyImageSet) {
          await supabase
            .from("memory_image_sets")
            .update({ is_active: false })
            .eq("id", familyImageSet.id);
        }

        await supabase
          .from("memory_image_sets")
          .update({ is_active: true })
          .eq("type", "random");
      }

      // Save points config to weekend_game_config
      if (activeWeekend?.id) {
        const { data: existing } = await supabase
          .from("weekend_game_config")
          .select("id")
          .eq("weekend_id", activeWeekend.id)
          .eq("game_slug", "puzzle_matching")
          .maybeSingle();

        if (existing) {
          await supabase
            .from("weekend_game_config")
            .update({ points_config: pointsConfig, updated_at: new Date().toISOString() })
            .eq("id", existing.id);
        } else {
          await supabase.from("weekend_game_config").insert({
            weekend_id: activeWeekend.id,
            game_slug: "puzzle_matching",
            is_enabled: true,
            points_config: pointsConfig,
          });
        }
      }

      await fetchImageSets();
      Alert.alert("Opgeslagen", "Memory instellingen zijn bijgewerkt");
    } catch (err) {
      console.error("Error saving image set:", err);
      Alert.alert("Fout", "Kon instellingen niet opslaan");
    } finally {
      setIsSaving(false);
    }
  }, [imageSource, familyImages, familyImageSet, activeWeekend?.id, fetchImageSets, pointsConfig]);

  /** How many weekend photos are currently selected */
  const selectedWeekendCount = useMemo(
    () => weekendPhotos.filter((p) => selectedUrlsSet.has(p.file_url)).length,
    [weekendPhotos, selectedUrlsSet]
  );

  /** Images that were manually uploaded (not from weekend or admin gallery) */
  const uploadedImages = useMemo(() => {
    const knownUrls = new Set([
      ...weekendPhotos.map((p) => p.file_url),
      ...adminPhotos.map((p) => p.file_url),
    ]);
    return familyImages.filter((url) => !knownUrls.has(url));
  }, [familyImages, weekendPhotos, adminPhotos]);

  if (isLoading) {
    return (
      <SafeAreaView className="flex-1 items-center justify-center" style={{ backgroundColor: colors.background }}>
        <ActivityIndicator size="large" color={colors.primary} />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }}>
      {/* Header */}
      <View
        className="flex-row items-center justify-between px-4 py-3 border-b"
        style={{ borderColor: colors.border }}
      >
        <TouchableOpacity
          onPress={() => router.back()}
          className="w-10 h-10 items-center justify-center"
          accessibilityLabel="Terug"
          accessibilityRole="button"
        >
          <Ionicons name="arrow-back" size={24} color={colors.text} />
        </TouchableOpacity>
        <Text className="font-bold text-lg" style={{ color: colors.text }}>
          Memory Instellingen
        </Text>
        <TouchableOpacity
          onPress={handleSave}
          disabled={isSaving}
          className="px-4 py-2 rounded-full"
          style={{ backgroundColor: colors.primary }}
          accessibilityLabel="Opslaan"
          accessibilityRole="button"
        >
          {isSaving ? (
            <ActivityIndicator size="small" color="#FFFFFF" />
          ) : (
            <Text className="font-semibold text-white text-sm">Opslaan</Text>
          )}
        </TouchableOpacity>
      </View>

      <ScrollView
        className="flex-1"
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={handleRefresh}
            tintColor={colors.primary}
          />
        }
      >
        {/* Points Configuration */}
        <View className="px-4 pt-4 pb-2">
          <TouchableOpacity
            onPress={() => setShowPoints(!showPoints)}
            className="flex-row items-center justify-between mb-2"
            accessibilityRole="button"
            accessibilityLabel="Punten instellingen"
          >
            <View className="flex-row items-center">
              <Ionicons name="trophy" size={18} color="#F59E0B" />
              <Text className="font-semibold text-base ml-2" style={{ color: colors.text }}>
                Punten instellingen
              </Text>
            </View>
            <Ionicons
              name={showPoints ? "chevron-up" : "chevron-down"}
              size={20}
              color={colors.textSecondary}
            />
          </TouchableOpacity>

          {!showPoints && (
            <Text className="text-sm mb-2" style={{ color: colors.textSecondary }}>
              Voltooiing: {pointsConfig.completion} · Snelheid: {pointsConfig.speed_bonus} · Perfect: {pointsConfig.perfect_memory_bonus}
            </Text>
          )}

          {showPoints && (
            <View className="rounded-xl p-4 mb-3" style={{ backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border }}>
              {[
                { key: "completion" as const, label: "Voltooiing", description: "Punten bij het voltooien", color: "#22C55E", step: 1 },
                { key: "speed_bonus" as const, label: "Snelheidsbonus", description: "Extra punten voor snel spelen", color: "#3B82F6", step: 1 },
                { key: "perfect_memory_bonus" as const, label: "Perfect Memory", description: "Bonus voor 0 foute zetten", color: "#F59E0B", step: 5 },
              ].map((field) => (
                <View key={field.key} className="mb-4 last:mb-0">
                  <Text className="text-sm font-medium mb-1" style={{ color: colors.text }}>
                    {field.label}
                  </Text>
                  <Text className="text-xs mb-2" style={{ color: colors.textSecondary }}>
                    {field.description}
                  </Text>
                  <View className="flex-row items-center">
                    <TouchableOpacity
                      onPress={() => setPointsConfig((prev) => ({ ...prev, [field.key]: Math.max(0, prev[field.key] - field.step) }))}
                      className="w-10 h-10 rounded-lg items-center justify-center"
                      style={{ backgroundColor: colors.surfaceSecondary }}
                      accessibilityLabel={`${field.label} verlagen`}
                      accessibilityRole="button"
                    >
                      <Ionicons name="remove" size={20} color={colors.text} />
                    </TouchableOpacity>
                    <TextInput
                      value={pointsConfig[field.key].toString()}
                      onChangeText={(text) => setPointsConfig((prev) => ({ ...prev, [field.key]: parseInt(text) || 0 }))}
                      keyboardType="number-pad"
                      className="flex-1 mx-2 py-2 rounded-lg text-center text-lg font-bold"
                      style={{ backgroundColor: colors.surfaceSecondary, color: field.color }}
                    />
                    <TouchableOpacity
                      onPress={() => setPointsConfig((prev) => ({ ...prev, [field.key]: prev[field.key] + field.step }))}
                      className="w-10 h-10 rounded-lg items-center justify-center"
                      style={{ backgroundColor: colors.surfaceSecondary }}
                      accessibilityLabel={`${field.label} verhogen`}
                      accessibilityRole="button"
                    >
                      <Ionicons name="add" size={20} color={colors.text} />
                    </TouchableOpacity>
                  </View>
                </View>
              ))}
            </View>
          )}

          <View className="h-px mb-4" style={{ backgroundColor: colors.border }} />
        </View>

        {/* Source Selection */}
        <View className="px-4 pt-0 pb-2">
          <Text className="font-semibold text-base mb-1" style={{ color: colors.text }}>
            Afbeeldingen bron
          </Text>
          <Text className="text-sm mb-4" style={{ color: colors.textSecondary }}>
            Kies welke afbeeldingen worden gebruikt in het Memory spel.
          </Text>

          {/* Standard Images Option */}
          <TouchableOpacity
            onPress={() => setImageSource("standard")}
            className="rounded-2xl p-4 mb-3"
            style={{
              backgroundColor: imageSource === "standard" ? `${colors.primary}10` : colors.surface,
              borderWidth: 2,
              borderColor: imageSource === "standard" ? colors.primary : colors.border,
            }}
            accessibilityRole="radio"
            accessibilityState={{ checked: imageSource === "standard" }}
            accessibilityLabel="Standaard afbeeldingen"
          >
            <View className="flex-row items-center">
              <View
                className="w-12 h-12 rounded-xl items-center justify-center mr-3"
                style={{
                  backgroundColor: imageSource === "standard" ? `${colors.primary}20` : `${colors.textTertiary}15`,
                }}
              >
                <Ionicons
                  name="images"
                  size={24}
                  color={imageSource === "standard" ? colors.primary : colors.textTertiary}
                />
              </View>
              <View className="flex-1">
                <Text className="font-semibold text-base" style={{ color: colors.text }}>
                  Standaard afbeeldingen
                </Text>
                <Text className="text-sm" style={{ color: colors.textSecondary }}>
                  {RANDOM_IMAGES.length} leuke plaatjes (robots, minions, etc.)
                </Text>
              </View>
              {imageSource === "standard" && (
                <Ionicons name="checkmark-circle" size={24} color={colors.primary} />
              )}
            </View>

            {imageSource === "standard" && (
              <View className="flex-row flex-wrap mt-3 gap-2">
                {STANDARD_PREVIEW_IMAGES.map((source, i) => (
                  <Image
                    key={i}
                    source={source}
                    className="w-12 h-12 rounded-lg"
                    style={{ backgroundColor: colors.surfaceSecondary }}
                  />
                ))}
                {RANDOM_IMAGES.length > STANDARD_PREVIEW_IMAGES.length && (
                  <View
                    className="w-12 h-12 rounded-lg items-center justify-center"
                    style={{ backgroundColor: colors.surfaceSecondary }}
                  >
                    <Text className="text-xs font-medium" style={{ color: colors.textSecondary }}>
                      +{RANDOM_IMAGES.length - STANDARD_PREVIEW_IMAGES.length}
                    </Text>
                  </View>
                )}
              </View>
            )}
          </TouchableOpacity>

          {/* Family Images Option */}
          <TouchableOpacity
            onPress={() => setImageSource("family")}
            className="rounded-2xl p-4 mb-3"
            style={{
              backgroundColor: imageSource === "family" ? "#EC489910" : colors.surface,
              borderWidth: 2,
              borderColor: imageSource === "family" ? "#EC4899" : colors.border,
            }}
            accessibilityRole="radio"
            accessibilityState={{ checked: imageSource === "family" }}
            accessibilityLabel="Familie foto's"
          >
            <View className="flex-row items-center">
              <View
                className="w-12 h-12 rounded-xl items-center justify-center mr-3"
                style={{
                  backgroundColor: imageSource === "family" ? "#EC489920" : `${colors.textTertiary}15`,
                }}
              >
                <Ionicons
                  name="people"
                  size={24}
                  color={imageSource === "family" ? "#EC4899" : colors.textTertiary}
                />
              </View>
              <View className="flex-1">
                <Text className="font-semibold text-base" style={{ color: colors.text }}>
                  Familie foto's
                </Text>
                <Text className="text-sm" style={{ color: colors.textSecondary }}>
                  {familyImages.length > 0
                    ? `${familyImages.length} foto's geselecteerd`
                    : "Selecteer uit weekend galerij of upload nieuwe"}
                </Text>
              </View>
              {imageSource === "family" && (
                <Ionicons name="checkmark-circle" size={24} color="#EC4899" />
              )}
            </View>
          </TouchableOpacity>
        </View>

        {/* Family Images Management */}
        {imageSource === "family" && (
          <View className="px-4 pt-2 pb-4">
            {/* Selected count + difficulty indicator */}
            <View
              className="rounded-xl p-3 mb-4 flex-row items-center justify-between"
              style={{ backgroundColor: `${colors.primary}10` }}
            >
              <View className="flex-row items-center">
                <Ionicons name="checkmark-done" size={20} color={colors.primary} />
                <Text className="ml-2 font-semibold" style={{ color: colors.primary }}>
                  {familyImages.length} geselecteerd
                </Text>
              </View>
              {familyImages.length < 6 ? (
                <View className="flex-row items-center px-2 py-1 rounded-full" style={{ backgroundColor: "#EF444420" }}>
                  <Ionicons name="warning" size={12} color="#EF4444" />
                  <Text className="text-xs ml-1 font-medium" style={{ color: "#EF4444" }}>
                    Min. 6 nodig
                  </Text>
                </View>
              ) : familyImages.length >= 15 ? (
                <View className="flex-row items-center px-2 py-1 rounded-full" style={{ backgroundColor: "#22C55E20" }}>
                  <Ionicons name="checkmark-circle" size={12} color="#22C55E" />
                  <Text className="text-xs ml-1 font-medium" style={{ color: "#22C55E" }}>
                    Alle modi
                  </Text>
                </View>
              ) : (
                <View className="flex-row items-center px-2 py-1 rounded-full" style={{ backgroundColor: "#F59E0B20" }}>
                  <Text className="text-xs font-medium" style={{ color: "#F59E0B" }}>
                    {familyImages.length >= 10 ? "3" : familyImages.length >= 8 ? "2" : "1"} van 4 modi
                  </Text>
                </View>
              )}
            </View>

            {/* Weekend Photos Section */}
            {weekendPhotos.length > 0 && (
              <View className="mb-6">
                <View className="flex-row items-center justify-between mb-3">
                  <View className="flex-row items-center">
                    <Ionicons name="calendar" size={18} color="#8B5CF6" />
                    <Text className="font-semibold ml-2" style={{ color: colors.text }}>
                      Weekend foto's ({weekendPhotos.length})
                    </Text>
                  </View>
                  <TouchableOpacity
                    onPress={
                      selectedWeekendCount === weekendPhotos.length
                        ? handleDeselectAllWeekendPhotos
                        : handleSelectAllWeekendPhotos
                    }
                    className="px-3 py-1 rounded-full"
                    style={{ backgroundColor: `${colors.primary}15` }}
                    accessibilityLabel={
                      selectedWeekendCount === weekendPhotos.length
                        ? "Deselecteer alle weekend foto's"
                        : "Selecteer alle weekend foto's"
                    }
                    accessibilityRole="button"
                  >
                    <Text className="text-xs font-medium" style={{ color: colors.primary }}>
                      {selectedWeekendCount === weekendPhotos.length
                        ? "Deselecteer alles"
                        : "Selecteer alles"}
                    </Text>
                  </TouchableOpacity>
                </View>

                <Text className="text-sm mb-3" style={{ color: colors.textSecondary }}>
                  Tik op een foto om deze toe te voegen of te verwijderen.
                </Text>

                <View className="flex-row flex-wrap" style={{ gap: GRID_GAP }}>
                  {weekendPhotos.map((photo) => {
                    const isSelected = selectedUrlsSet.has(photo.file_url);
                    return (
                      <TouchableOpacity
                        key={photo.id}
                        onPress={() => handleTogglePhoto(photo.file_url)}
                        className="relative"
                        activeOpacity={0.7}
                        accessibilityRole="checkbox"
                        accessibilityState={{ checked: isSelected }}
                        accessibilityLabel="Weekend foto selecteren"
                      >
                        <Image
                          source={{ uri: photo.thumbnail_url || photo.file_url }}
                          className="rounded-xl"
                          style={{
                            width: IMAGE_SIZE,
                            height: IMAGE_SIZE,
                            backgroundColor: colors.surfaceSecondary,
                            opacity: isSelected ? 1 : 0.5,
                          }}
                          resizeMode="cover"
                        />
                        {/* Selection overlay */}
                        {isSelected && (
                          <View
                            className="absolute top-1 right-1 w-6 h-6 rounded-full items-center justify-center"
                            style={{ backgroundColor: "#22C55E" }}
                          >
                            <Ionicons name="checkmark" size={16} color="#FFFFFF" />
                          </View>
                        )}
                        {!isSelected && (
                          <View
                            className="absolute top-1 right-1 w-6 h-6 rounded-full items-center justify-center"
                            style={{
                              backgroundColor: "rgba(0,0,0,0.3)",
                              borderWidth: 2,
                              borderColor: "#FFFFFF",
                            }}
                          />
                        )}
                      </TouchableOpacity>
                    );
                  })}
                </View>
              </View>
            )}

            {/* Loading weekend photos */}
            {isLoadingPhotos && (
              <View className="py-6 items-center mb-4">
                <ActivityIndicator color={colors.primary} />
                <Text className="mt-2 text-sm" style={{ color: colors.textSecondary }}>
                  Weekend foto's laden...
                </Text>
              </View>
            )}

            {/* No weekend photos */}
            {!isLoadingPhotos && weekendPhotos.length === 0 && activeWeekend && (
              <View
                className="rounded-xl p-4 mb-4 flex-row items-start"
                style={{ backgroundColor: colors.surfaceSecondary }}
              >
                <Ionicons name="images-outline" size={20} color={colors.textTertiary} />
                <Text className="ml-2 flex-1 text-sm" style={{ color: colors.textSecondary }}>
                  Er zijn nog geen foto's gedeeld tijdens "{activeWeekend.name}". Je kunt hieronder zelf foto's uploaden.
                </Text>
              </View>
            )}

            {/* Divider after weekend photos */}
            {weekendPhotos.length > 0 && (
              <View className="h-px mb-4" style={{ backgroundColor: colors.border }} />
            )}

            {/* Admin Photos Section */}
            {adminPhotos.length > 0 && (
              <View className="mb-6">
                <View className="flex-row items-center justify-between mb-3">
                  <View className="flex-row items-center">
                    <Ionicons name="person-circle" size={18} color="#F59E0B" />
                    <Text className="font-semibold ml-2" style={{ color: colors.text }}>
                      Admin galerij ({adminPhotos.length})
                    </Text>
                  </View>
                  <TouchableOpacity
                    onPress={
                      adminPhotos.every((p) => selectedUrlsSet.has(p.file_url))
                        ? () => {
                            const adminUrls = new Set(adminPhotos.map((p) => p.file_url));
                            setFamilyImages((prev) => prev.filter((url) => !adminUrls.has(url)));
                          }
                        : () => {
                            const allUrls = adminPhotos.map((p) => p.file_url);
                            setFamilyImages((prev) => {
                              const existing = new Set(prev);
                              const newUrls = allUrls.filter((url) => !existing.has(url));
                              return [...prev, ...newUrls];
                            });
                          }
                    }
                    className="px-3 py-1 rounded-full"
                    style={{ backgroundColor: "#F59E0B15" }}
                    accessibilityLabel="Selecteer alle admin foto's"
                    accessibilityRole="button"
                  >
                    <Text className="text-xs font-medium" style={{ color: "#F59E0B" }}>
                      {adminPhotos.every((p) => selectedUrlsSet.has(p.file_url))
                        ? "Deselecteer alles"
                        : "Selecteer alles"}
                    </Text>
                  </TouchableOpacity>
                </View>

                <Text className="text-sm mb-3" style={{ color: colors.textSecondary }}>
                  Foto's van jou en andere familie admins.
                </Text>

                <View className="flex-row flex-wrap" style={{ gap: GRID_GAP }}>
                  {adminPhotos.map((photo) => {
                    const isSelected = selectedUrlsSet.has(photo.file_url);
                    const uploaderName = photo.uploader?.nickname || photo.uploader?.full_name || "";
                    return (
                      <TouchableOpacity
                        key={photo.id}
                        onPress={() => handleTogglePhoto(photo.file_url)}
                        className="relative"
                        activeOpacity={0.7}
                        accessibilityRole="checkbox"
                        accessibilityState={{ checked: isSelected }}
                        accessibilityLabel={`Admin foto van ${uploaderName}`}
                      >
                        <Image
                          source={{ uri: photo.thumbnail_url || photo.file_url }}
                          className="rounded-xl"
                          style={{
                            width: IMAGE_SIZE,
                            height: IMAGE_SIZE,
                            backgroundColor: colors.surfaceSecondary,
                            opacity: isSelected ? 1 : 0.5,
                          }}
                          resizeMode="cover"
                        />
                        {/* Selection overlay */}
                        {isSelected && (
                          <View
                            className="absolute top-1 right-1 w-6 h-6 rounded-full items-center justify-center"
                            style={{ backgroundColor: "#22C55E" }}
                          >
                            <Ionicons name="checkmark" size={16} color="#FFFFFF" />
                          </View>
                        )}
                        {!isSelected && (
                          <View
                            className="absolute top-1 right-1 w-6 h-6 rounded-full items-center justify-center"
                            style={{
                              backgroundColor: "rgba(0,0,0,0.3)",
                              borderWidth: 2,
                              borderColor: "#FFFFFF",
                            }}
                          />
                        )}
                        {/* Uploader name badge */}
                        {uploaderName && (
                          <View
                            className="absolute bottom-1 left-1 right-1 px-1 py-0.5 rounded-md"
                            style={{ backgroundColor: "rgba(0,0,0,0.6)" }}
                          >
                            <Text className="text-white text-center" style={{ fontSize: 9 }} numberOfLines={1}>
                              {uploaderName}
                            </Text>
                          </View>
                        )}
                      </TouchableOpacity>
                    );
                  })}
                </View>

                {/* Divider after admin photos */}
                <View className="h-px mt-4 mb-4" style={{ backgroundColor: colors.border }} />
              </View>
            )}

            {/* Upload new photos section */}
            <View className="flex-row items-center mb-3">
              <Ionicons name="cloud-upload" size={18} color="#EC4899" />
              <Text className="font-semibold ml-2" style={{ color: colors.text }}>
                Nieuwe foto's uploaden
              </Text>
            </View>

            <TouchableOpacity
              onPress={handlePickImages}
              disabled={isUploading}
              className="rounded-xl p-4 mb-4 flex-row items-center justify-center"
              style={{
                backgroundColor: "#EC489910",
                borderWidth: 2,
                borderColor: "#EC489930",
                borderStyle: "dashed",
              }}
              accessibilityLabel="Foto's uploaden"
              accessibilityRole="button"
            >
              {isUploading ? (
                <>
                  <ActivityIndicator size="small" color="#EC4899" />
                  <Text className="ml-2 font-medium" style={{ color: "#EC4899" }}>
                    Uploaden...
                  </Text>
                </>
              ) : (
                <>
                  <Ionicons name="add-circle" size={24} color="#EC4899" />
                  <Text className="ml-2 font-medium" style={{ color: "#EC4899" }}>
                    Foto's kiezen uit galerij
                  </Text>
                </>
              )}
            </TouchableOpacity>

            {/* Uploaded images (not from weekend gallery) */}
            {uploadedImages.length > 0 && (
              <View className="mb-4">
                <Text className="text-sm mb-2" style={{ color: colors.textSecondary }}>
                  Geüpload ({uploadedImages.length})
                </Text>
                <View className="flex-row flex-wrap" style={{ gap: GRID_GAP }}>
                  {uploadedImages.map((url, index) => (
                    <View key={`uploaded-${index}`} className="relative">
                      <Image
                        source={{ uri: url }}
                        className="rounded-xl"
                        style={{
                          width: IMAGE_SIZE,
                          height: IMAGE_SIZE,
                          backgroundColor: colors.surfaceSecondary,
                        }}
                        resizeMode="cover"
                      />
                      <TouchableOpacity
                        onPress={() => handleRemoveImage(url)}
                        className="absolute -top-2 -right-2 w-7 h-7 rounded-full items-center justify-center shadow-sm"
                        style={{ backgroundColor: "#EF4444" }}
                        accessibilityLabel={`Geüploade foto ${index + 1} verwijderen`}
                        accessibilityRole="button"
                      >
                        <Ionicons name="close" size={16} color="#FFFFFF" />
                      </TouchableOpacity>
                    </View>
                  ))}
                </View>
              </View>
            )}

            {/* Difficulty availability */}
            {familyImages.length > 0 && (
              <View className="mt-2">
                <Text className="font-medium mb-2" style={{ color: colors.text }}>
                  Beschikbare moeilijkheidsgraden
                </Text>
                {[
                  { label: "Makkelijk (3x4)", pairs: 6, color: "#10B981" },
                  { label: "Normaal (4x4)", pairs: 8, color: "#3B82F6" },
                  { label: "Moeilijk (4x5)", pairs: 10, color: "#F59E0B" },
                  { label: "Expert (5x6)", pairs: 15, color: "#EF4444" },
                ].map((diff) => {
                  const available = familyImages.length >= diff.pairs;
                  return (
                    <View
                      key={diff.label}
                      className="flex-row items-center justify-between py-2 px-3 rounded-lg mb-1"
                      style={{ backgroundColor: available ? `${diff.color}10` : colors.surfaceSecondary }}
                    >
                      <View className="flex-row items-center">
                        <Ionicons
                          name={available ? "checkmark-circle" : "close-circle"}
                          size={18}
                          color={available ? diff.color : colors.textTertiary}
                        />
                        <Text className="ml-2 text-sm font-medium" style={{ color: available ? colors.text : colors.textTertiary }}>
                          {diff.label}
                        </Text>
                      </View>
                      <Text className="text-xs" style={{ color: available ? diff.color : colors.textTertiary }}>
                        {diff.pairs} foto's nodig
                      </Text>
                    </View>
                  );
                })}
              </View>
            )}

            {/* Empty state when nothing selected and no uploads */}
            {familyImages.length === 0 && uploadedImages.length === 0 && weekendPhotos.length === 0 && !isLoadingPhotos && (
              <View
                className="rounded-xl p-8 items-center"
                style={{ backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border }}
              >
                <Ionicons name="images-outline" size={48} color={colors.textTertiary} />
                <Text className="mt-3 font-medium text-center" style={{ color: colors.textSecondary }}>
                  Nog geen foto's
                </Text>
                <Text className="mt-1 text-sm text-center" style={{ color: colors.textTertiary }}>
                  Upload foto's of wacht tot familieleden foto's delen
                </Text>
              </View>
            )}
          </View>
        )}

        {/* Bottom padding */}
        <View className="h-8" />
      </ScrollView>
    </SafeAreaView>
  );
}
